import {
  CloudLightningIcon,
  CloudSunRainIcon,
  DropletsIcon,
  GaugeIcon,
  GlobeIcon,
  GraduationCapIcon,
  ThermometerIcon,
  TornadoIcon,
  WindIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  classroom: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Fundamentos de Meteorología y Ciencias de la Atmósfera',
    subtitle: 'La base que tus alumnos necesitan para entender todo lo demás',
    classroom:
      'El primer contacto con la meteorología puede ser el más transformador o el más aburrido. Este tomo convierte un tema técnico en algo comprensible y relevante para tus alumnos desde el primer día.',
    items: [
      'Conceptos fundamentales de meteorología',
      'Tiempo y clima: diferencias y relaciones',
      'La atmósfera como sistema',
      'Variables meteorológicas principales',
      'Escalas de análisis meteorológico',
      'Observación meteorológica',
      'Predicción del tiempo',
      'Principales campos de las ciencias atmosféricas',
      'Vocabulario esencial',
      'Actividades de aplicación y reflexión',
    ],
    result:
      'Tus alumnos entienden la meteorología como una disciplina práctica y relevante, no como un conjunto de términos imposibles de memorizar.',
    icon: CloudSunRainIcon,
  },
  {
    number: '2',
    title: 'La Atmósfera: Composición, Estructura, Energía y Temperatura',
    subtitle: 'Todo lo que necesitan saber sobre la atmósfera y sus características',
    classroom:
      'Este es uno de los puntos donde más se traban los alumnos. Este tomo te da el material listo para que comprendan la atmósfera como sistema energético.',
    items: [
      'Composición de la atmósfera',
      'Gases principales y su importancia',
      'Estructura vertical de la atmósfera',
      'Capas atmosféricas',
      'Radiación solar y balance energético',
      'Absorción y reflexión de radiación',
      'Efecto invernadero',
      'Temperatura atmosférica',
      'Variaciones térmicas',
      'Estabilidad atmosférica',
      'Inversiones térmicas',
      'Factores que determinan la temperatura',
      '12 casos prácticos de análisis térmico',
    ],
    result: 'Tus alumnos aprenden cómo la energía solar impulsa todos los procesos atmosféricos.',
    icon: ThermometerIcon,
  },
  {
    number: '3',
    title: 'Humedad, Nubes, Precipitaciones y Procesos Atmosféricos',
    subtitle: 'Todo sobre el agua en la atmósfera y sus transformaciones',
    classroom:
      'La humedad y las nubes son fascinantes pero complejas. Este tomo te da todo lo que necesitas para que tus alumnos comprendan estos procesos.',
    items: [
      'Evaporación y evapotranspiración',
      'Humedad absoluta y relativa',
      'Saturación y punto de rocío',
      'Condensación',
      'Formación de nubes',
      'Clasificación de nubes',
      'Tipos de precipitación',
      'Mecanismos de formación de lluvia',
      'Nieve y granizo',
      'Ciclo hidrológico',
      'Procesos atmosféricos relacionados con el agua',
      '15 casos prácticos de análisis de humedad y nubes',
    ],
    result:
      'Tus alumnos comprenden la importancia del agua en la atmósfera y cómo genera la mayoría de los fenómenos meteorológicos.',
    icon: DropletsIcon,
  },
  {
    number: '4',
    title: 'Presión, Vientos y Circulación Atmosférica',
    subtitle: 'Cómo se mueve la atmósfera y qué genera el viento',
    classroom:
      'La presión y los vientos son fundamentales. Este tomo te da las herramientas para explicar la circulación atmosférica claramente.',
    items: [
      'Presión atmosférica',
      'Gradientes de presión',
      'Medición de la presión',
      'Formación del viento',
      'Fuerzas que controlan el movimiento del aire',
      'Fuerza de Coriolis',
      'Fricción del terreno',
      'Sistemas de altas presiones (anticiclones)',
      'Sistemas de bajas presiones (ciclones)',
      'Circulación general de la atmósfera',
      'Corrientes en altura y jet streams',
      'Circulaciones locales',
      'Brisas y vientos locales',
      '12 casos prácticos de análisis de presión y vientos',
    ],
    result: 'Tus alumnos entienden por qué sopla el viento y cómo se organiza la circulación global.',
    icon: WindIcon,
  },
  {
    number: '5',
    title: 'Masas de Aire, Frentes y Sistemas Meteorológicos',
    subtitle: 'Cómo se originan y evolucionan los sistemas meteorológicos',
    classroom:
      'Los frentes y sistemas meteorológicos son complejos pero fascinantes. Este tomo te da todo para que tus alumnos entiendan los cambios del tiempo.',
    items: [
      'Masas de aire y su origen',
      'Características de las masas de aire',
      'Tipos de masas de aire',
      'Frentes meteorológicos',
      'Frentes fríos',
      'Frentes cálidos',
      'Frentes ocluidos',
      'Interacción entre masas de aire',
      'Evolución de sistemas meteorológicos',
      'Depresiones y sus características',
      'Predicción de cambios del tiempo',
      '15 casos de análisis de sistemas meteorológicos',
    ],
    result: 'Tus alumnos pueden analizar mapas sinópticos y predecir cambios meteorológicos.',
    icon: TornadoIcon,
  },
  {
    number: '6',
    title: 'Tormentas, Fenómenos Severos y Riesgos Meteorológicos',
    subtitle: 'Fenómenos extremos y cómo afectan a las comunidades',
    classroom:
      'Los fenómenos severos son importantes pero requieren comprensión científica. Este tomo conecta la ciencia con la realidad.',
    items: [
      'Formación y evolución de tormentas',
      'Tormentas convectivas',
      'Características de una tormenta severa',
      'Granizo y su formación',
      'Tornados',
      'Identificación de riesgos meteorológicos',
      'Fenómenos meteorológicos extremos',
      'Inundaciones y lluvias torrenciales',
      'Sequías y estrés hídrico',
      'Vientos extremos',
      'Prevención y preparación ante riesgos',
      'Casos de eventos extremos en Latinoamérica',
      'Cambio climático y fenómenos extremos',
      '12 casos prácticos de análisis de riesgos',
    ],
    result: 'Tus alumnos entienden los riesgos meteorológicos y cómo las comunidades pueden prepararse.',
    icon: CloudLightningIcon,
  },
  {
    number: '7',
    title: 'Observación, Instrumentos y Datos Meteorológicos',
    subtitle: 'Métodos e instrumentos para observar la atmósfera',
    classroom: 'La observación es central en la meteorología. Este tomo te da herramientas prácticas para el aula.',
    items: [
      'Métodos de observación meteorológica',
      'Instrumentos meteorológicos principales',
      'Termómetros y medición de temperatura',
      'Barómetros y medición de presión',
      'Anemómetros y medición de vientos',
      'Pluviómetros y medición de precipitación',
      'Higrómetros y medición de humedad',
      'Heliógrafos y radiación solar',
      'Nefoscopios y observación de nubes',
      'Estaciones meteorológicas',
      'Redes de observación',
      'Calidad de los datos',
      'Registro e interpretación de datos',
      '10 experiencias de observación meteorológica',
    ],
    result: 'Tus alumnos aprenden a observar, registrar e interpretar datos meteorológicos de manera profesional.',
    icon: GaugeIcon,
  },
  {
    number: '8',
    title: 'Pronóstico, Clima y Variabilidad Atmosférica en Latinoamérica',
    subtitle: 'Predicción del tiempo y características climáticas de la región',
    classroom: 'Este tomo conecta la teoría meteorológica con la realidad de Latinoamérica.',
    items: [
      'Interpretación meteorológica',
      'Elaboración de pronósticos',
      'Comunicación de pronósticos',
      'Incertidumbre en los pronósticos',
      'Variabilidad climática',
      'Oscilaciones climáticas',
      'El Niño y La Niña',
      'Variabilidad estacional',
      'Climas de Latinoamérica',
      'Regiones climáticas principales',
      'Fenómenos específicos de la región',
      'Monsones americanos',
      'Anticiclones del Atlántico Sur',
      'Corriente de Humboldt',
      'Cambio climático en Latinoamérica',
      '18 casos de análisis meteorológico y climático regional',
    ],
    result: 'Tus alumnos comprenden la meteorología y el clima de su región y del continente.',
    icon: GlobeIcon,
  },
  {
    number: '9',
    title: 'Enseñanza de Meteorología: Planificación, Actividades y Proyectos 2026',
    subtitle: 'Tu guía completa como docente',
    classroom: 'Este tomo te da exactamente lo que necesitas para planificar, organizar y evaluar todo el año.',
    items: [
      'Cómo enseñar meteorología',
      'Objetivos y resultados de aprendizaje',
      'Organización de contenidos',
      'Planificación de clases',
      'Secuencias didácticas',
      'Actividades individuales y grupales',
      'Aprendizaje basado en problemas',
      'Casos prácticos',
      'Proyectos educativos',
      'Enseñanza de observación meteorológica',
      'Enseñanza de análisis sinóptico',
      'Enseñanza de pronóstico',
      'Enseñanza de riesgos meteorológicos',
      'Enseñanza de cambio climático',
      'Evaluación, rúbricas y criterios',
      'Adaptación a diferentes contextos educativos',
      'Proyecto docente integrador',
      '10 proyectos integradores listos para adaptar',
    ],
    result: 'Nunca más te preguntas “¿qué doy hoy?”. Todo está planificado y contextualizado.',
    icon: GraduationCapIcon,
  },
];
